import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth } from "../auth/AuthContext";
import config from "../config";
import {
  ResizableColGroup,
  ResizableHeaderCell,
  TableEmptyRow,
  TableLoadingRow,
  useResizableColumns,
} from "../components/table/ResizableTable";

const PRODUCT_TYPE_OPTIONS = [
  "flexi case",
  "magnetic case",
  "snap case",
  "tough case",
];
const VARIANT_TYPE_OPTIONS = ["android", "iphone"];
const TABLE_COLUMNS = [
  { key: "title", label: "Title", minWidth: 200, maxWidth: 520, defaultWidth: 280 },
  { key: "count", label: "Active Listings", minWidth: 120, maxWidth: 260, defaultWidth: 150 },
  { key: "rank", label: "Rank", minWidth: 90, maxWidth: 180, defaultWidth: 100 },
  { key: "product_type", label: "Product Type", minWidth: 140, maxWidth: 280, defaultWidth: 180 },
  { key: "variant_type", label: "Variant Type", minWidth: 140, maxWidth: 280, defaultWidth: 170 },
];

const SectionsPage = () => {
  const { fetchWithAuth } = useAuth();
  const [shops, setShops] = React.useState([]);
  const [selectedShopId, setSelectedShopId] = React.useState("");
  const [sections, setSections] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [shopsLoading, setShopsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [search, setSearch] = React.useState("");
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [newSectionTitle, setNewSectionTitle] = React.useState("");
  const [newSectionProductType, setNewSectionProductType] = React.useState("");
  const [newSectionVariantType, setNewSectionVariantType] = React.useState("");
  const [newSectionMockupInstructions, setNewSectionMockupInstructions] = React.useState("");
  const [dialogError, setDialogError] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editingSection, setEditingSection] = React.useState(null);
  const [editSectionTitle, setEditSectionTitle] = React.useState("");
  const [editSectionProductType, setEditSectionProductType] =
    React.useState("");
  const [editSectionVariantType, setEditSectionVariantType] =
    React.useState("");
  const [editSectionMockupInstructions, setEditSectionMockupInstructions] =
    React.useState("");
  const [editError, setEditError] = React.useState(null);
  const [updating, setUpdating] = React.useState(false);
  const { getWidth, startColumnResize, tableMinWidth } = useResizableColumns(TABLE_COLUMNS, {
    storageKey: "table.etsy-sections",
  });

  const loadShops = React.useCallback(async () => {
    setShopsLoading(true);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/accounts`);
      if (!resp.ok) {
        throw new Error("Failed to load Etsy accounts");
      }
      const payload = await resp.json();
      const mapped = Array.isArray(payload.items)
        ? payload.items
            .map((acct) => acct.shop)
            .filter(Boolean)
            .map((shop) => ({
              id: shop.id,
              title: shop.title || shop.name,
              externalId: shop.external_id,
            }))
        : [];
      setShops(mapped);
      if (!selectedShopId && mapped.length > 0) {
        setSelectedShopId(mapped[0].id);
      }
    } catch (err) {
      setError(err.message || "Failed to load shops");
    } finally {
      setShopsLoading(false);
    }
  }, [fetchWithAuth]);

  React.useEffect(() => {
    loadShops();
  }, [loadShops]);

  const loadSections = React.useCallback(async () => {
    if (!selectedShopId) {
      setSections([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("shop_id", selectedShopId);
      if (search.trim()) {
        params.set("q", search.trim());
      }
      const resp = await fetchWithAuth(
        `${config.apiBaseUrl}/etsy/sections?${params.toString()}`,
      );
      if (!resp.ok) {
        throw new Error("Failed to load sections");
      }
      const payload = await resp.json();
      setSections(Array.isArray(payload.items) ? payload.items : []);
    } catch (err) {
      setError(err.message || "Failed to load sections");
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, selectedShopId, search]);

  React.useEffect(() => {
    loadSections();
  }, [loadSections]);

  const handleSync = async () => {
    if (!selectedShopId) {
      return;
    }
    setSyncing(true);
    setError(null);
    try {
      const resp = await fetchWithAuth(
        `${config.apiBaseUrl}/etsy/sections/sync`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ shop_id: selectedShopId }),
        },
      );
      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        throw new Error(payload.error || "Failed to sync sections");
      }
      await loadSections();
    } catch (err) {
      setError(err.message || "Failed to sync sections");
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateSection = async (event) => {
    event.preventDefault();
    if (!selectedShopId) {
      setDialogError("Select a shop first");
      return;
    }
    if (!newSectionTitle.trim()) {
      setDialogError("Title is required");
      return;
    }
    setCreating(true);
    setDialogError(null);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/sections`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          shop_id: selectedShopId,
          title: newSectionTitle.trim(),
          product_type: newSectionProductType,
          variant_type: newSectionVariantType,
          mockup_instructions: newSectionMockupInstructions,
        }),
      });
      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        throw new Error(payload.error || "Failed to create section");
      }
      await loadSections();
      setDialogOpen(false);
      setNewSectionTitle("");
      setNewSectionProductType("");
      setNewSectionVariantType("");
      setNewSectionMockupInstructions("");
    } catch (err) {
      setDialogError(err.message || "Failed to create section");
    } finally {
      setCreating(false);
    }
  };

  const handleOpenEditDialog = (section) => {
    setEditingSection(section);
    setEditSectionTitle(section.title || "");
    setEditSectionProductType(section.product_type || "");
    setEditSectionVariantType(section.variant_type || "");
    setEditSectionMockupInstructions(section.mockup_instructions || "");
    setEditError(null);
    setEditDialogOpen(true);
  };

  const handleUpdateSection = async (event) => {
    event.preventDefault();
    if (!editingSection) {
      return;
    }
    setUpdating(true);
    setEditError(null);
    try {
      const payload = {
        title: editSectionTitle,
        product_type: editSectionProductType,
        variant_type: editSectionVariantType,
        mockup_instructions: editSectionMockupInstructions,
      };
      const resp = await fetchWithAuth(
        `${config.apiBaseUrl}/etsy/sections/${editingSection.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || "Failed to update section");
      }
      await loadSections();
      setEditDialogOpen(false);
      setEditingSection(null);
    } catch (err) {
      setEditError(err.message || "Failed to update section");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ md: "center" }}
      >
        <TextField
          select
          label="Etsy Shop"
          value={selectedShopId}
          onChange={(event) => setSelectedShopId(event.target.value)}
          sx={{ minWidth: 240 }}
          disabled={shopsLoading || shops.length === 0}
        >
          {shops.length === 0 ? (
            <MenuItem value="" disabled>
              {shopsLoading ? "Loading…" : "No shops"}
            </MenuItem>
          ) : (
            shops.map((shop) => (
              <MenuItem key={shop.id} value={shop.id}>
                {shop.title || shop.externalId}
              </MenuItem>
            ))
          )}
        </TextField>
        <TextField
          fullWidth
          placeholder="Search sections"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <SearchIcon
                fontSize="small"
                sx={{ mr: 1, color: "text.secondary" }}
              />
            ),
          }}
        />
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          sx={{ width: { xs: "100%", md: "auto" } }}
        >
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleSync}
            disabled={!selectedShopId || syncing}
          >
            {syncing ? "Syncing…" : "Sync Sections"}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            disabled={!selectedShopId}
          >
            Add Section
          </Button>
        </Stack>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Paper sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <TableContainer sx={{ flex: 1, overflowY: "auto" }}>
          <Table size="small" sx={{ tableLayout: "fixed", minWidth: tableMinWidth }} stickyHeader>
            <ResizableColGroup columns={TABLE_COLUMNS} getWidth={getWidth} />
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map((column) => (
                  <ResizableHeaderCell
                    key={column.key}
                    column={column}
                    getWidth={getWidth}
                    onResizeStart={startColumnResize}
                  />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingRow colSpan={TABLE_COLUMNS.length} label="Loading sections..." />
              ) : sections.length === 0 ? (
                <TableEmptyRow colSpan={TABLE_COLUMNS.length} label="No sections found." />
              ) : (
                sections.map((section) => (
                  <TableRow
                    key={section.id || section.external_id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleOpenEditDialog(section)}
                  >
                    <TableCell>{section.title}</TableCell>
                    <TableCell>{section.count}</TableCell>
                    <TableCell>{section.rank}</TableCell>
                    <TableCell>{section.product_type || "—"}</TableCell>
                    <TableCell>{section.variant_type || "—"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setDialogError(null);
          setNewSectionMockupInstructions("");
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Section</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Section Title"
            value={newSectionTitle}
            onChange={(event) => setNewSectionTitle(event.target.value)}
            autoFocus
            margin="dense"
          />
          <TextField
            select
            fullWidth
            label="Product Type"
            value={newSectionProductType}
            onChange={(event) => setNewSectionProductType(event.target.value)}
            margin="dense"
          >
            <MenuItem value="">None</MenuItem>
            {PRODUCT_TYPE_OPTIONS.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Variant Type"
            value={newSectionVariantType}
            onChange={(event) => setNewSectionVariantType(event.target.value)}
            margin="dense"
          >
            <MenuItem value="">None</MenuItem>
            {VARIANT_TYPE_OPTIONS.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Mockup Instructions"
            value={newSectionMockupInstructions}
            onChange={(event) => setNewSectionMockupInstructions(event.target.value)}
            margin="dense"
          />
          {dialogError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dialogError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
              setDialogError(null);
            }}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateSection}
            variant="contained"
            disabled={creating}
          >
            {creating ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingSection(null);
          setEditError(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Section</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Section Title"
            value={editSectionTitle}
            onChange={(event) => setEditSectionTitle(event.target.value)}
            margin="dense"
          />
          <TextField
            select
            fullWidth
            label="Product Type"
            value={editSectionProductType}
            onChange={(event) => setEditSectionProductType(event.target.value)}
            margin="dense"
          >
            <MenuItem value="">None</MenuItem>
            {PRODUCT_TYPE_OPTIONS.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Variant Type"
            value={editSectionVariantType}
            onChange={(event) => setEditSectionVariantType(event.target.value)}
            margin="dense"
          >
            <MenuItem value="">None</MenuItem>
            {VARIANT_TYPE_OPTIONS.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Mockup Instructions"
            value={editSectionMockupInstructions}
            onChange={(event) => setEditSectionMockupInstructions(event.target.value)}
            margin="dense"
          />
          {editError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {editError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              setEditingSection(null);
              setEditError(null);
            }}
            disabled={updating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateSection}
            variant="contained"
            disabled={updating || !editingSection}
          >
            {updating ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SectionsPage;
